import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

/**
 * QL-313 — a legend that carries figures: one chip per series under a chart,
 * a colour swatch (or a dashed-line glyph for a reference line), the series
 * name and its value at the point that matters. Used by the compliance charts
 * between the chart and the data table, so the bands are named with their
 * gating figures once, without a second table next to the per-point one.
 * Chart legends are turned off where this strip is shown. An item may carry a
 * `hint`, shown on hover: the compliance charts use it to say which date the
 * year-1 figure is read at, since that point is drawn nowhere.
 */

export type ChartLegendStripItem = {
  key: string;
  label: string;
  /** Formatted value, e.g. "22.5%". */
  value: string;
  /** Swatch colour; omitted for a `line` item, which draws a dashed glyph. */
  color?: string;
  /** `swatch` (default) is a filled square; `line` is a dashed rule (a ceiling, a baseline). */
  glyph?: "swatch" | "line";
  /** `destructive` reads the value in red (a breach); `muted` greys the chip. */
  tone?: "default" | "destructive" | "muted";
  /** Hover text for the chip, e.g. the date a year-1 figure is read at. */
  hint?: string;
};

type ChartLegendStripProps = {
  items: ChartLegendStripItem[];
  className?: string;
};

export function ChartLegendStrip({ items, className }: ChartLegendStripProps) {
  if (items.length === 0) return null;
  return (
    <ul className={cn("flex flex-wrap items-center gap-x-5 gap-y-2 text-sm", className)}>
      {items.map((item) => {
        const chip = (
          <span className="flex items-center gap-2">
            {item.glyph === "line" ? (
              <span
                aria-hidden
                className="inline-block w-4 border-t-2 border-dashed"
                style={{ borderColor: item.color ?? "currentColor" }}
              />
            ) : (
              <span
                aria-hidden
                className="inline-block h-3 w-3 rounded-sm"
                style={{ backgroundColor: item.color ?? "currentColor" }}
              />
            )}
            <span>{item.label}</span>
            <span
              className={cn(
                "tabular-nums font-medium",
                item.tone === "destructive" ? "text-destructive" : "",
              )}
            >
              {item.value}
            </span>
          </span>
        );
        return (
          <li key={item.key} className={item.tone === "muted" ? "text-muted-foreground" : ""}>
            {item.hint ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="cursor-default">{chip}</span>
                </TooltipTrigger>
                <TooltipContent>{item.hint}</TooltipContent>
              </Tooltip>
            ) : (
              chip
            )}
          </li>
        );
      })}
    </ul>
  );
}
